/**
 * An organization's settings (§5.25) — one page per compartment of the desktop
 * app's own settings, written by the owner and the administrators and **read by
 * every member**.
 *
 * A member reads it because this document is not the organization's private
 * business: it is what their own machine is handed on every check, and a locked
 * row here is a decision about their app that they cannot make locally. So the
 * page is drawn for them with no verb and no live control — the value, and the
 * padlock where there is one. Publishing is refused by the Worker
 * (`putOrgSettings` answers 404 to anybody who is not an owner or an admin), so
 * `editable` here is about not offering what would be refused, never about the
 * refusal itself.
 *
 * What used to be here: a `textarea` holding twenty-six settings as JSON, a
 * second one holding what a new install is handed, and a grid of thirty-two
 * checkboxes on another card that had to be kept in agreement with the first
 * two by hand. What is here now is a row per setting, of the type the
 * validator keeps — `oneOf` is a select of its own values, `int` is a number
 * with its bounds written beside it, `bool` is a switch — so a 422 on one key
 * is no longer reachable from this page.
 *
 * Two rules the layout makes visible, which the JSON could only imply:
 *
 * - **A row is a value and a lock, and nothing else.** *Override* and *Reset*
 *   are both gone. `Override` was a click in front of every change whose only
 *   effect was to let somebody touch the field they were already looking at;
 *   `Reset` was its undo, and undoing it is not something anybody came to this
 *   page to do — a row's value is what the organization prefers, and preferring
 *   the same number the application does is a perfectly good answer to leave
 *   written down. The control is live from the start, showing what the
 *   application would use, and **changing it is the answer**.
 * - **A value and its lock are one row.** `parseOrgSettings` refuses a setting
 *   "enforced with no value to enforce", so locking a row nobody has answered
 *   writes the value it is showing in the same act rather than refusing the
 *   click. A locked row keeps its control: the lock is a promise made to
 *   *members* about their machines, not a decision the organization makes about
 *   itself, and a page where changing a value means unlocking it first is a
 *   page that makes people unlock things.
 *
 * Saving carries the version the page was opened on, so two administrators
 * cannot silently overwrite each other.
 */
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { DEFAULT_SETTINGS } from '@airlock/contracts/settings/defaults';
import { ONBOARDING_SETTINGS } from '@airlock/contracts/org/onboarding';
import { color, font } from '../../theme/tokens';
import { AirlockDefault, USAGE_KEY, UsageHint, usageWord } from './usage-setting';
import { HeaderButton } from '../../components/Shell';
import { PageError, SkeletonPage, useLoadPhase } from '../../components/Loading';
import { Note, PageBody, PageLede } from '../../components/list';
import { Empty, GhostButton } from '../../components/primitives';
import { useConfirm } from '../../components/Modal';
import {
  InheritedValue,
  LockToggle,
  LockedValue,
  SettingNumber,
  SettingRow,
  SettingSelect,
  SettingSet,
  SettingText,
  SettingToggle
} from '../../components/settings-row';
import { api, ifMatch, apiPath } from '../../lib/api';
import type {
  EnforcedSetting,
  Organization,
  OrgDefaults,
  OrgSettings,
  OrgSettingsConflict,
  OrgSettingsReach,
  OrgSettingsRecord
} from '../../lib/types';
import { PublishSettings } from './PublishSettings';
import { changesBetween } from './settings-diff';
import { DockerfileField } from './DockerfileField';
import { ModelsPolicy, NetworkPolicy } from './org-policy';
import { PullRequestPolicy } from './pull-request-policy';
import { RulesPolicy } from './rules-policy-settings';
import { TargetsPolicy } from './targets-policy-settings';
import { ExtensionLibraryPolicy } from './extension-library-policy';
import { ExtensionsField } from './ExtensionsField';
import { AutoLandField } from './AutoLandField';
import { AuditField } from './AuditField';
import { GithubAppPanel } from './GithubAppPanel';
import { GithubReposPanel } from './GithubReposPanel';
import { SharedSignInNote } from './SharedSignInNote';
import type { AgentId } from '@airlock/contracts/settings/agents';
import { enabledAgents } from '@airlock/contracts/settings/models';
import type { SettingsDocument } from '@airlock/contracts/settings/agent-settings/documents';

const AgentCatalogue = lazy(() => import('./agents/AgentCatalogue'));
import {
  AGENT_SECTION_SPECS,
  INTEGRATION_SECTION_SPECS,
  MODEL_EFFORT_OPTIONS,
  SECTION_SPECS,
  isDocumentRow,
  pairOf,
  splitPair,
  type SettingSpec
} from './org-catalogue';
import { conflictOf, failureText, problemsUnder, refusalProblems } from './shared';
import { showValue } from './setting-values';
import { since } from '../../lib/format';
import { PublishReceipt } from '../../components/PublishReceipt';

const EMPTY: OrgSettings = { defaults: {}, enforced: [], onboarding: {} };

type Values = Record<string, unknown>;

/** What the app itself answers, which is what an unanswered row inherits. */
const APP: Values = DEFAULT_SETTINGS as unknown as Values;

/**
 * One page, three families of section. They differ in which compartments they
 * hold, never in how a row behaves — which is the point: an administrator
 * learns the row once and reads every page of this area with it.
 */
const FAMILIES = {
  settings: SECTION_SPECS,
  agents: AGENT_SECTION_SPECS,
  integrations: INTEGRATION_SECTION_SPECS
} as const;

export function OrgSettingsPage({
  organization,
  section,
  family = 'settings',
  admin,
  onPublished
}: {
  organization: Organization | null;
  section: string | null;
  family?: keyof typeof FAMILIES;
  /** Whether this account may publish. A member reads the same page without verbs. */
  admin: boolean;
  /** The rail draws what this page decides — which integrations exist, which
   *  agents are offered — so it is told when that answer moves. */
  onPublished?: () => void;
}): React.JSX.Element {
  const [record, setRecord] = useState<OrgSettingsRecord | null>(null);
  const [draft, setDraft] = useState<OrgSettings>(EMPTY);
  const [problems, setProblems] = useState<string[]>([]);
  const [dialog, ask] = useConfirm();
  const [conflict, setConflict] = useState<OrgSettingsConflict | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Whether the publish is being read before it is made, and who it would land
  // on. The reach is read once with the settings rather than when the dialog
  // opens: it is two small counts, and a dialog that appears empty and fills in
  // half a second is one people press through.
  const [asking, setAsking] = useState(false);
  const [reach, setReach] = useState<OrgSettingsReach | null>(null);
  const orgId = organization?.id ?? null;

  /** Take their version, losing what is on this page. Asked first, because
   *  nothing on screen has been published and there is no undo for it — the
   *  modal S of §19, what goes being each edit here and what stays their
   *  version (item 86b). */
  const reloadTheirs = async (): Promise<void> => {
    const theirs = conflict?.current;
    if (!theirs) return;
    const edits = changesBetween(record?.settings ?? EMPTY, draft);
    const agreed = await ask({
      title: 'Reload their version?',
      goes: edits.map((change) => `Your edit to ${change.label}: ${change.words ?? change.now}.`),
      stays: [`Version ${theirs.version} as it was published, every section.`],
      confirm: 'Reload their version',
      danger: true
    });
    if (!agreed) return;
    await load();
    const who = theirs.updatedBy?.label;
    setNote(
      `${who ? `${who}’s version` : 'Version'} ${theirs.version} is loaded. ${edits.length === 1 ? 'Your edit was' : 'Your edits were'} dropped.`
    );
  };

  const load = useCallback(async (): Promise<void> => {
    if (!orgId) return;
    const settings = await api.get<OrgSettingsRecord>(apiPath`/orgs/${orgId}/settings`);
    setRecord(settings);
    setDraft(settings.settings);
    setProblems([]);
    setConflict(null);
  }, [orgId]);

  useEffect(() => {
    load().catch((failure: unknown) => setError(failureText(failure, 'The settings could not be read.')));
  }, [load]);

  // Nothing for 300 ms, then the skeleton (§33, #120) rather than « Reading … ».
  const phase = useLoadPhase(!record && !error);

  // Silently, on purpose: the dialog is drawn either way, and it says what
  // changed whether or not it can say who it lands on. A reach that could not
  // be read is a missing sentence, never a missing publish.
  //
  // Not asked for at all by a reader: the reach counts other people's machines
  // and the Worker answers it 404 to anybody but an administrator, so a member
  // opening this page would send one request whose only possible answer is a
  // refusal — for a dialog they are never shown.
  useEffect(() => {
    if (!orgId || !admin) return;
    let live = true;
    void api
      .get<OrgSettingsReach>(apiPath`/orgs/${orgId}/settings/reach`)
      .then((answer) => live && setReach(answer))
      .catch(() => live && setReach(null));
    return () => {
      live = false;
    };
  }, [orgId, admin]);

  if (!organization) {
    return (
      <PageBody>
        <Empty title="No organization">
          These settings belong to an organization, and this account holds no seat in one.
        </Empty>
      </PageBody>
    );
  }
  if (!record) {
    return (
      <PageBody>
        {error ? (
          <PageError
            onRetry={() =>
              load()
                .then(() => setError(null))
                .catch((failure: unknown) => setError(failureText(failure, 'The settings could not be read.')))
            }
          />
        ) : (
          phase === 'skeleton' && <SkeletonPage />
        )}
      </PageBody>
    );
  }

  const specs = FAMILIES[family];
  const first = Object.keys(specs)[0];
  const spec = specs[section ?? first] ?? specs[first];
  const defaults = draft.defaults as Values;
  const onboarding = draft.onboarding as Values;
  const held = (layer: SettingSpec['layer']): Values => (layer === 'onboarding' ? onboarding : defaults);

  /** What this organization has answered for, if anything. */
  const answered = (key: string, layer?: SettingSpec['layer']): boolean => {
    const value = held(layer)[key];
    return value !== undefined && value !== null;
  };
  const effective = (key: string, layer?: SettingSpec['layer']): unknown =>
    answered(key, layer) ? held(layer)[key] : APP[key];
  const locked = (key: string): boolean => draft.enforced.includes(key as EnforcedSetting);

  /**
   * An agent this organization does not offer has no configuration page.
   *
   * Its catalogue configures something no member here can launch, which is the
   * same thing a withheld integration's page would be — and the rail drops the
   * row for that reason (`nav.ts`). What is set is left in the document
   * untouched: withdrawing an agent is not a decision to forget how it was
   * configured, and switching it back on brings the page back as it was.
   */
  const withdrawnAgent =
    spec.agentDocument !== undefined &&
    !enabledAgents(effective('agents', 'onboarding') as string[] | undefined).includes(spec.agentDocument as AgentId);

  /**
   * A whole layer as it applies — what a reader is shown.
   *
   * The record rather than one value, because one row is two keys: the model
   * and the effort are chosen as a pair, and `showValue` prints half of it when
   * handed the model alone.
   */
  const applied = (layer?: SettingSpec['layer']): Values => ({
    ...APP,
    ...Object.fromEntries(Object.entries(held(layer)).filter(([, value]) => value !== undefined && value !== null))
  });

  const write = (patch: Values, layer?: SettingSpec['layer']): void =>
    setDraft((current) =>
      layer === 'onboarding'
        ? { ...current, onboarding: { ...current.onboarding, ...patch } }
        : { ...current, defaults: { ...current.defaults, ...patch } as OrgDefaults }
    );

  const toggleLock = (keys: string[], layer?: SettingSpec['layer']): void =>
    setDraft((current) => {
      const on = current.enforced.includes(keys[0] as EnforcedSetting);
      if (on) {
        return { ...current, enforced: current.enforced.filter((one) => !keys.includes(one)) };
      }
      const where = layer === 'onboarding' ? 'onboarding' : 'defaults';
      const values = { ...(current[where] as Values) };
      for (const key of keys) if (values[key] === undefined || values[key] === null) values[key] = APP[key] ?? null;
      return {
        ...current,
        [where]: values,
        enforced: [
          ...current.enforced,
          ...(keys.filter((key) => !current.enforced.includes(key as EnforcedSetting)) as EnforcedSetting[])
        ]
      } as OrgSettings;
    });

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setNote(null);
    setProblems([]);
    setConflict(null);
    setAsking(false);
    try {
      const saved = await api.send<OrgSettingsRecord>('PUT', apiPath`/orgs/${organization.id}/settings`, { settings: draft }, ifMatch(record.version));
      await load();
      onPublished?.();
      // Not every layer lands the same way, and saying otherwise is the kind of
      // promise somebody plans around. A default is a layer an install reads on
      // every check. What sets a machine *up* — the agents offered, their
      // catalogues, the ticket boards — is applied in full only when a machine
      // first joins; afterwards a machine takes the locked ones silently and
      // puts the rest to its member as a change to accept.
      const asked = ONBOARDING_SETTINGS.filter(
        (key) => key in (draft.onboarding as Record<string, unknown>) && !draft.enforced.includes(key)
      );
      setNote(
        asked.length
          ? `Published version ${saved.version}. Installs read it at their next check — but a machine already set up keeps its own ${asked.join(', ')} until its member accepts the change. Lock one to have it applied without asking.`
          : `Published version ${saved.version}. Members’ installs read it at their next check.`
      );
    } catch (failure) {
      const refused = refusalProblems(failure);
      const lost = conflictOf<OrgSettingsConflict>(failure);
      if (refused) setProblems(refused);
      else if (lost) setConflict(lost);
      else setError(failureText(failure));
    } finally {
      setBusy(false);
    }
  };

  // What the published version said, per row the draft has moved (§11.2
  // « changé »): the row carries the amber dot and « Was … » until Publish or
  // Discard, so a reader of the page sees which rows the count in the bar is.
  const changes = changesBetween(record.settings, draft);
  const moved = new Map(changes.map((change) => [change.key, change.was]));

  /** One row, whichever kind it is and whichever layer it writes to. */
  const row = (rowSpec: SettingSpec): React.JSX.Element => {
    const key = rowSpec.key;
    const layer = rowSpec.layer;
    // The model and the effort are one row over two keys, so everything that
    // acts on a row acts on both of them.
    const keys = rowSpec.kind === 'modelEffort' ? [key, 'defaultEffort'] : [key];
    const isLocked = locked(key);
    const value = effective(key, layer);
    const hidden = rowSpec.needs && effective(rowSpec.needs.key, layer) !== rowSpec.needs.value;
    const put = (patch: Values): void => write(patch, layer);
    const settingKey = rowSpec.kind === 'modelEffort' ? 'defaultModel · defaultEffort' : key;

    // Usage statistics: collection is a value of true and nothing else, so an
    // unanswered row reads Not collected whatever the app's default is, and the
    // three lines under it say which of the three answers it is (#163).
    if (key === USAGE_KEY) {
      const source = admin ? held(layer) : applied(layer);
      const state = { answered: source[key] !== undefined && source[key] !== null, on: source[key] === true, locked: isLocked };
      return (
        <SettingRow
          key={key}
          name={rowSpec.label}
          settingKey={settingKey}
          hint={<UsageHint state={state} org={organization.name} />}
          was={admin ? moved.get(key) : undefined}
          value={
            admin ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {state.answered ? null : <AirlockDefault />}
                <span style={{ fontSize: 12.5, color: color.inkMuted }}>{usageWord(state.on)}</span>
                <SettingToggle on={state.on} onToggle={() => put({ [key]: !state.on })} />
              </span>
            ) : isLocked ? (
              <LockedValue by="locked">{usageWord(state.on)}</LockedValue>
            ) : (
              <InheritedValue from={state.answered ? organization.name : 'APP'}>{usageWord(state.on)}</InheritedValue>
            )
          }
          action={admin ? <LockToggle on={isLocked} disabled={!state.answered} onToggle={() => toggleLock(keys, layer)} /> : undefined}
        />
      );
    }

    // A reader gets the value and nothing to change it with — no live control,
    // no padlock to press. The two sentences it can be said in are the whole
    // difference that matters to them: **locked** is a setting their own app
    // will not let them touch either, and an ordinary row is a default their
    // app starts from and may still move. `APP` where the organization has said
    // nothing at all, because "what Airlock itself does" is a different answer
    // from "what this organization decided", and a page that printed both the
    // same way would be inventing decisions nobody took.
    if (!admin) {
      const shown = showValue(rowSpec, applied(layer));
      return (
        <SettingRow
          key={key}
          name={rowSpec.label}
          settingKey={settingKey}
          hint={rowSpec.hint}
          wide={isDocumentRow(rowSpec.kind)}
          depends={hidden ? rowSpec.needs?.note : undefined}
          value={
            isDocumentRow(rowSpec.kind) ? null : isLocked ? (
              <LockedValue by="locked">{shown}</LockedValue>
            ) : (
              <InheritedValue from={answered(key, layer) ? organization.name : 'APP'}>{shown}</InheritedValue>
            )
          }
        >
          {rowSpec.kind === 'wide' && (
            <DockerfileField value={String(applied(layer).dockerfile ?? '')} locked onChange={() => undefined} />
          )}
          {rowSpec.kind === 'extensions' && (
            <ExtensionsField
              value={applied(layer).agentExtensions as never}
              editable={false}
              onChange={() => undefined}
            />
          )}
          {rowSpec.kind === 'autoLand' && (
            <AutoLandField value={applied(layer).autoLand} editable={false} onChange={() => undefined} />
          )}
          {rowSpec.kind === 'audit' && (
            <AuditField value={applied(layer).audit} editable={false} onChange={() => undefined} />
          )}
        </SettingRow>
      );
    }

    // Every row draws its control, answered or not. What it shows when nothing
    // has been answered is the value the application would use, so changing it
    // *is* the answer — which is the whole of what `Override` used to be, minus
    // the click that only meant "yes, I would like to type something".
    const control = ((): React.ReactNode => {
      if (rowSpec.kind === 'bool') return <SettingToggle on={Boolean(value)} onToggle={() => put({ [key]: !value })} />;
      if (rowSpec.kind === 'agents') {
        const chosen = Array.isArray(value) ? (value as string[]) : [];
        return <SettingSet value={chosen} options={rowSpec.options ?? []} onChange={(next) => put({ [key]: next })} />;
      }
      if (rowSpec.kind === 'oneOf') {
        return <SettingSelect value={String(value ?? '')} options={rowSpec.options ?? []} onChange={(next) => put({ [key]: next })} />;
      }
      if (rowSpec.kind === 'modelEffort') {
        return (
          <SettingSelect
            value={pairOf(String(defaults.defaultModel ?? APP.defaultModel), String(defaults.defaultEffort ?? APP.defaultEffort))}
            options={MODEL_EFFORT_OPTIONS}
            onChange={(pair) => put(splitPair(pair) as Values)}
          />
        );
      }
      if (rowSpec.kind === 'int') {
        const shown = rowSpec.scale ? Math.round(Number(value) / rowSpec.scale) : Number(value);
        return (
          <SettingNumber
            value={Number.isFinite(shown) ? shown : (rowSpec.min ?? 0)}
            min={rowSpec.min ?? 0}
            max={rowSpec.max ?? 0}
            step={rowSpec.step}
            unit={rowSpec.unit}
            onChange={(next) => put({ [key]: rowSpec.scale ? next * rowSpec.scale : next })}
          />
        );
      }
      // A value the row's own test refuses, or the Worker refused at Publish,
      // is said under the field (item 89b); a field whose switch is off is
      // held with the reason (89c).
      const text = String(value ?? '');
      const check = rowSpec.check;
      const answeredNo = problemsUnder(problems, `${layer ?? 'defaults'}.${key}`).length > 0;
      const refused = check && (!check.accepts(text) || answeredNo);
      return (
        <SettingText
          value={text}
          placeholder={rowSpec.placeholder}
          onChange={(next) => put({ [key]: next })}
          invalid={refused ? check.refused : undefined}
          held={hidden ? rowSpec.needs?.held : undefined}
        />
      );
    })();

    return (
      <SettingRow
        key={key}
        name={rowSpec.label}
        settingKey={settingKey}
        hint={rowSpec.hint}
        wide={isDocumentRow(rowSpec.kind)}
        depends={hidden ? rowSpec.needs?.note : undefined}
        value={isDocumentRow(rowSpec.kind) ? null : control}
        was={moved.get(key)}
        action={<LockToggle on={isLocked} onToggle={() => toggleLock(keys, layer)} />}
      >
        {rowSpec.kind === 'wide' && (
          <DockerfileField
            value={String(defaults.dockerfile ?? APP.dockerfile ?? '')}
            locked={false}
            onChange={(next) => put({ dockerfile: next })}
          />
        )}
        {rowSpec.kind === 'extensions' && (
          <ExtensionsField
            value={(defaults.agentExtensions ?? APP.agentExtensions) as never}
            onChange={(next) => put({ agentExtensions: next as never })}
          />
        )}
        {rowSpec.kind === 'autoLand' && (
          <AutoLandField value={defaults.autoLand ?? APP.autoLand} onChange={(next) => put({ autoLand: next })} />
        )}
        {rowSpec.kind === 'audit' && (
          <AuditField value={defaults.audit ?? APP.audit} onChange={(next) => put({ audit: next })} />
        )}
      </SettingRow>
    );
  };

  const unclaimed = problems.filter((problem) => !problemsUnder(problems, 'defaults').includes(problem));

  const pending = changes.length;

  return (
    <>
      {dialog}
      {/* A reader's bar says which version they are reading and stops there.
          The verb is absent rather than disabled: the Worker refuses the
          publish, and a greyed button is a page describing what somebody else
          is allowed to do. */}
      {/* The publish bar (redesign §13): sticky under the header on the site's
          frosted glass; the draft's change count on the left, Discard just left
          of Publish on the right. A reader gets the read band, no verb. */}
      <div
        role="region"
        aria-label="Draft"
        style={{
          position: 'sticky',
          top: 52,
          zIndex: 9,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          minHeight: 52,
          margin: '16px 0 0',
          padding: '0 var(--al-gutter)',
          background: conflict ? color.warnBg : admin ? 'rgba(255,255,255,.8)' : color.surfaceAlt,
          backdropFilter: admin && !conflict ? 'saturate(180%) blur(20px)' : undefined,
          borderBottom: `1px solid ${color.line}`
        }}
      >
        {admin ? (
          <>
            {conflict ? (
              // The conflict state (§44.1, item 86a): their version is what is
              // published now, so the bar says so and offers the two ways on;
              // Publish is held until one is taken.
              <span aria-live="polite" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 12px', padding: '8px 0' }}>
                <span style={{ fontSize: 13.5, color: color.warnText }}>
                  <strong style={{ fontWeight: 600 }}>Changed since you opened it.</strong> Version{' '}
                  {conflict.current.version} was published {since(conflict.current.updatedAt)}.
                </span>
                <span style={{ display: 'flex', gap: 8 }}>
                  <HeaderButton onClick={() => void reloadTheirs()}>Reload their version</HeaderButton>
                  <GhostButton size="sm" onClick={() => setConflict(null)}>
                    Keep editing
                  </GhostButton>
                </span>
              </span>
            ) : pending > 0 ? (
              <span aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: color.warnText }}>
                <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: '#C9A03A' }} />
                {pending === 1 ? '1 change, not published' : `${pending} changes, not published`}
              </span>
            ) : (
              <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkMuted }}>
                v{record.version} published · every section, not only this one
              </span>
            )}
            <span style={{ flex: 1 }} />
            {pending > 0 && !busy && !conflict && <HeaderButton onClick={() => setDraft(record.settings)}>Discard</HeaderButton>}
            {/* The verb opens the diff rather than publishing. What is behind it
                is a whole version every install reads, and one of the things it
                can write freezes a setting on somebody else's machine — so the
                button asks, and the dialog is where the publish is actually
                made. Held while nothing differs from what is published
                (proposal 27). */}
            <HeaderButton
              primary
              onClick={() => setAsking(true)}
              disabled={busy}
              reason={
                busy
                  ? undefined
                  : conflict
                    ? 'Reload their version, or keep editing, first'
                    : pending === 0
                      ? 'Nothing to publish yet'
                      : undefined
              }
            >
              {busy ? 'Publishing' : `Publish v${record.version + 1}`}
            </HeaderButton>
          </>
        ) : (
          <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkMuted }}>
            v{record.version} · what your installs are handed
          </span>
        )}
      </div>

      {!admin && (
        <div style={{ padding: '16px var(--al-gutter) 0' }}>
          <Note>
            This is what {organization.name} publishes to every machine in it, and it is here to{' '}
            <strong style={{ fontWeight: 600 }}>read</strong>. A row marked <strong style={{ fontWeight: 600 }}>locked</strong>{' '}
            is one Airlock will not let you change on your own machine either; the rest are where your app starts, and you
            can still move them there. Changing them for everybody is for whoever owns or administers this organization.
          </Note>
        </div>
      )}

      {asking && (
        <PublishSettings
          version={record.version + 1}
          changes={changes}
          reach={reach}
          publishedAt={record.updatedAt}
          busy={busy}
          onConfirm={() => void save()}
          onCancel={() => setAsking(false)}
        />
      )}

      <PageBody gap={0}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 6 }}>
          {note && <PublishReceipt text={note} codes={ONBOARDING_SETTINGS} />}
          <PageLede>{spec.lede}</PageLede>
          {spec.notes?.map((text) => (
            <Note key={text}>{text}</Note>
          ))}
        </div>

        {withdrawnAgent && (
          <Note>
            {organization.name} does not offer this agent, so there is nothing to configure for it here. Offer it again
            under <strong style={{ fontWeight: 600 }}>All agents</strong> and its configuration comes back as it was.
          </Note>
        )}

        {spec.agentDocument && !withdrawnAgent && (
          // Lazy on purpose: the agents' catalogue is 608 KB of vendored JSON,
          // and nothing that is not this page should carry it.
          <Suspense fallback={<SkeletonPage blocks={2} />}>
            <AgentCatalogue
              agent={spec.agentDocument as AgentId}
              document={((onboarding.agentSettings as Values | undefined)?.[spec.agentDocument] ?? {}) as SettingsDocument}
              editable={admin}
              onChange={(next) =>
                write(
                  {
                    agentSettings: {
                      ...((onboarding.agentSettings as Values | undefined) ?? {}),
                      [spec.agentDocument as string]: next
                    }
                  },
                  'onboarding'
                )
              }
            />
          </Suspense>
        )}

        {spec.key === 'github' && family === 'integrations' && admin && <GithubAppPanel orgId={organization.id} />}
        {spec.key === 'github' && family === 'integrations' && <GithubReposPanel orgId={organization.id} admin={admin} />}
        {(spec.key === 'jira' || spec.key === 'trello') && family === 'integrations' && (
          <SharedSignInNote orgId={organization.id} kind={spec.key} />
        )}

        {spec.groups.map((group, index) => (
          <div key={group.label ?? index}>
            {group.label && (
              <div
                style={{
                  paddingTop: 20,
                  paddingBottom: 4,
                  fontFamily: font.mono,
                  fontSize: 10.5,
                  letterSpacing: '.12em',
                  textTransform: 'uppercase',
                  color: color.inkFaint
                }}
              >
                {group.label}
              </div>
            )}
            {group.rows.map(row)}
          </div>
        ))}

        {spec.key === 'all' && family === 'agents' && (
          <ExtensionLibraryPolicy
            settings={draft}
            editable={admin}
            onChange={(policy) => setDraft((current) => ({ ...current, policy }))}
          />
        )}

        {spec.key === 'all' && family === 'agents' && (
          <ModelsPolicy
            settings={draft}
            offered={enabledAgents(effective('agents', 'onboarding') as string[] | undefined)}
            editable={admin}
            onChange={(policy) => setDraft((current) => ({ ...current, policy }))}
          />
        )}

        {spec.key === 'github' && family === 'integrations' && (
          <PullRequestPolicy
            settings={draft}
            editable={admin}
            onChange={(policy) => setDraft((current) => ({ ...current, policy }))}
          />
        )}

        {spec.key === 'git' && family === 'settings' && (
          <RulesPolicy
            settings={draft}
            editable={admin}
            onChange={(policy) => setDraft((current) => ({ ...current, policy }))}
          />
        )}

        {spec.key === 'network' && (
          <NetworkPolicy
            settings={draft}
            editable={admin}
            onChange={(policy) => setDraft((current) => ({ ...current, policy }))}
          />
        )}
        {spec.key === 'network' && (
          <TargetsPolicy
            settings={draft}
            editable={admin}
            onChange={(policy) => setDraft((current) => ({ ...current, policy }))}
          />
        )}

        <div style={{ paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {problems.length > 0 && (
            <Note tone="warn">
              <strong style={{ fontWeight: 600 }}>The server refused this document.</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {[...problemsUnder(problems, 'defaults'), ...unclaimed].map((problem) => (
                  <li key={problem} style={{ fontFamily: font.mono, fontSize: 11.5 }}>
                    {problem}
                  </li>
                ))}
              </ul>
            </Note>
          )}
          {error && <p style={{ fontSize: 13, color: color.badText, margin: 0 }}>{error}</p>}
          <p style={{ fontSize: 12, color: color.inkFaint, margin: 0, lineHeight: 1.6, maxWidth: 640 }}>
            {admin
              ? 'Publishing sends every section, not only this one. A row left inherited does not enter the document at all — each member keeps their own value for it.'
              : 'A row this organization has not answered is not in the document at all: your install keeps its own value for it, and the APP tag is what Airlock itself would do.'}
          </p>
        </div>
      </PageBody>
    </>
  );
}
